import { useMutation } from '@apollo/client'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { useForm } from 'react-hook-form'
import { Button, ButtonColor, NotificationType } from '../../../components'
import { UPDATE_CREATOR } from '../../../gql'
import {
  ChildAgeRange,
  Creator,
  CreatorPetsOwner,
  MutationUpdateCreatorArgs,
} from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import {
  childAgeRangeLocalizedString,
  childAgeRangeLocalizedStringInverse,
  petLocalizedString,
  petLocalizedStringInverse,
} from '../../../utils/GqlStrings'
import { ChildrenSelector, PetsSelector } from '..'

interface IProps {
  creator: Creator
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  setHasPetsAndChildren?: (value: boolean) => void
}

type FormData = {
  petsOwner: string[]
  childAgeRanges: string[]
}

const SetPetsAndChildrenModal = ({
  creator,
  isOpen,
  setIsOpen,
  setHasPetsAndChildren,
}: IProps) => {
  const { addNotification } = useNotification()
  const [updateCreator, { loading }] =
    useMutation<MutationUpdateCreatorArgs>(UPDATE_CREATOR)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      petsOwner: creator.petsOwner?.map(petLocalizedString) || [],
      childAgeRanges:
        creator.childAgeRanges?.map(childAgeRangeLocalizedString) || [],
    },
  })

  const closeModal = () => {
    setIsOpen(false)
    reset()
  }

  const onSubmit = async (data: FormData) => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            petsOwner:
              data.petsOwner && data.petsOwner.length > 0
                ? (data.petsOwner
                    .map((petOwner) => petLocalizedStringInverse(petOwner))
                    .filter(
                      (value) => value !== null && value !== undefined,
                    ) as CreatorPetsOwner[])
                : null,
            childAgeRanges:
              data.childAgeRanges && data.childAgeRanges.length > 0
                ? (data.childAgeRanges
                    .map((childAgeRange) =>
                      childAgeRangeLocalizedStringInverse(childAgeRange),
                    )
                    .filter(
                      (value) => value !== null && value !== undefined,
                    ) as ChildAgeRange[])
                : null,
          },
        },
      })

      // Update the parent component state
      if (setHasPetsAndChildren) {
        // Update only if both petsOwner and childAgeRanges exist
        const hasPetsAndChildrenData =
          data.petsOwner?.length > 0 && data.childAgeRanges?.length > 0

        setHasPetsAndChildren(hasPetsAndChildrenData)
      }

      addNotification('Profile updated successfully', NotificationType.success)
      closeModal()
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                <Dialog.Title
                  as='h3'
                  className='text-lg font-medium leading-6 text-gray-900'
                >
                  Children & Pets Information
                </Dialog.Title>
                <div className='mt-4'>
                  <p className='text-sm text-gray-500'>
                    Sharing your children and pets details helps us match you
                    with more relevant campaigns and opportunities that align
                    with your lifestyle.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className='mt-6 space-y-6'
                >
                  <PetsSelector
                    name='petsOwner'
                    control={control}
                    errors={errors}
                    required={true}
                    menuPlacement='top'
                  />

                  <ChildrenSelector
                    name='childAgeRanges'
                    control={control}
                    errors={errors}
                    required={true}
                    menuPlacement='top'
                  />

                  <div className='mt-6 flex justify-end space-x-3'>
                    <Button
                      title='Cancel'
                      type='button'
                      colour={ButtonColor.lightGray}
                      onClick={closeModal}
                    />
                    <Button
                      title='Save'
                      type='submit'
                      colour={ButtonColor.primary}
                      loading={loading}
                    />
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { SetPetsAndChildrenModal }
